import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  UserGroupIcon, 
  KeyIcon, 
  ArrowLeftIcon, 
  MagnifyingGlassIcon,
  LockClosedIcon,
  UsersIcon,
  ClockIcon,
  PlusIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { makeApiRequest } from '../utils/api';
import config from '../config';

function JoinRoom() {
  const navigate = useNavigate();
  const [publicRooms, setPublicRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [privateRoomName, setPrivateRoomName] = useState('');
  const [privateRoomPin, setPrivateRoomPin] = useState('');
  const [joinPrivateLoading, setJoinPrivateLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('public');

  // Fetch public rooms when component mounts
  const fetchPublicRooms = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Fetching public rooms...');
      const apiUrl = config.apiUrl;
      console.log('API URL:', apiUrl);
      
      // Now use the makeApiRequest utility
      const data = await makeApiRequest('api/rooms/public', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Public rooms response:', data);
      
      // The data should have rooms nested within data object
      const roomsData = data.rooms || 
                        (data.data && data.data.rooms) || 
                        (data.data && data.data) || 
                        [];
      
      if (!roomsData || !Array.isArray(roomsData)) {
        console.error('Invalid rooms data format:', data);
        throw new Error('Server returned invalid rooms data format');
      }
      
      if (roomsData.length === 0) {
        console.log('No public rooms found - this is not an error');
      }
      
      setPublicRooms(roomsData);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      setError(error.message || 'Failed to load rooms');
      toast.error('Failed to load public rooms. Please try again later.');
      
      // Set empty array to avoid undefined errors
      setPublicRooms([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicRooms();
  }, []);

  const handleJoinPublicRoom = (roomId) => {
    navigate(`/chat/${roomId}`);
    toast.success('Joined room successfully!');
  };

  // Join a private room
  const handleJoinPrivateRoom = async (e) => {
    e.preventDefault();
    
    if (!privateRoomName.trim()) {
      toast.error('Please enter a room name');
      return;
    }
    
    if (!privateRoomPin || privateRoomPin.length !== 4) {
      toast.error('Please enter a valid 4-digit PIN');
      return;
    }
    
    const nickname = localStorage.getItem('chatNickname');
    if (!nickname) {
      navigate('/');
      return;
    }
    
    setJoinPrivateLoading(true);
    
    try {
      console.log('Joining private room:', privateRoomName);
      
      const data = await makeApiRequest('api/rooms/join-private', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roomName: privateRoomName,
          pin: privateRoomPin,
          nickname
        })
      });
      
      console.log('Join private room response:', data);
      
      // Extract roomId from the nested data property or from the top level
      const roomId = data.data?.roomId || data.roomId;
      
      if (!roomId) {
        console.error('Missing roomId in response:', data);
        throw new Error('Server response missing room ID');
      }
      
      navigate(`/chat/${roomId}`);
      toast.success(data.message || 'Joined private room successfully!');
    } catch (error) {
      console.error('Error joining private room:', error);
      toast.error(error.message || 'Invalid room name or PIN');
    } finally {
      setJoinPrivateLoading(false);
    }
  };

  // Filter public rooms based on search term
  const filteredRooms = publicRooms.filter(room => 
    room.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    room.createdBy.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-indigo-950/30 dark:to-purple-950/30 flex flex-col items-start justify-start relative overflow-hidden px-4 py-8 transition-colors">
      {/* Background elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5 dark:opacity-10"></div>
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-200/20 via-transparent to-transparent dark:from-blue-800/10"></div>
      
      {/* Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto pt-6 pb-12">
        {/* Header with back button */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Home
          </button>
          
          <h1 className="text-4xl font-bold text-gray-800 dark:text-white mt-4">
            Join a Chat Room
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            Connect with others in existing chat rooms or create your own
          </p>
        </div>

        {/* Top section - Create room button */}
        <div className="mb-8 flex justify-between items-center">
          <div className="flex space-x-6">
            {/* Tab Navigation */}
            <button
              onClick={() => setActiveTab('public')}
              className={`py-2 px-4 font-medium rounded-full transition-colors flex items-center ${
                activeTab === 'public'
                  ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                  : 'bg-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/60'
              }`}
            >
              <UserGroupIcon className="w-5 h-5 mr-2" />
              Public Rooms
            </button>
            
            <button
              onClick={() => setActiveTab('private')}
              className={`py-2 px-4 font-medium rounded-full transition-colors flex items-center ${
                activeTab === 'private'
                  ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400'
                  : 'bg-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/60'
              }`}
            >
              <KeyIcon className="w-5 h-5 mr-2" />
              Private Room
            </button>
          </div>
          
          <button
            onClick={() => navigate('/create-room')}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 text-white py-2 px-5 rounded-xl flex items-center font-medium hover:shadow-lg transition-all hover:from-blue-600 hover:to-indigo-700 transform hover:-translate-y-0.5"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create New Room
          </button>
        </div>

        {/* Public Rooms Tab */}
        {activeTab === 'public' && (
          <div className="animate-fade-in">
            {/* Search bar */}
            <div className="mb-8">
              <div className="relative max-w-md">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by room name or creator..."
                  className="w-full pl-12 pr-4 py-3 border-2 rounded-xl border-gray-200 dark:border-gray-600 
                  bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-800 dark:text-white focus:border-blue-500 
                  dark:focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900/50 transition-all"
                />
              </div>
            </div>

            {/* Room cards */}
            {isLoading ? (
              <div className="flex flex-col justify-center items-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400 animate-pulse">Loading available rooms...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-6 rounded-xl border border-red-100 dark:border-red-800/50 max-w-lg mx-auto text-center">
                <p className="font-medium mb-2">Unable to load rooms</p>
                <p className="text-sm mb-4">{error}. Please try again later.</p>
                <button 
                  onClick={fetchPublicRooms}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : filteredRooms.length === 0 ? (
              <div className="text-center py-16 px-4 bg-white/40 dark:bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-100 dark:border-gray-700">
                <div className="bg-gray-50 dark:bg-gray-800 inline-flex items-center justify-center p-5 rounded-full mb-4 border border-gray-100 dark:border-gray-700">
                  <SparklesIcon className="h-12 w-12 text-blue-400 dark:text-blue-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-3">No rooms found</h3>
                <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-6">
                  {searchTerm 
                    ? `No rooms match your search "${searchTerm}"`
                    : "Be the first to create a public chat room and start the conversation!"
                  }
                </p>
                <button
                  onClick={() => navigate('/create-room')}
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 text-white rounded-xl font-medium transition-all hover:shadow-lg shadow-blue-500/20 hover:from-blue-600 hover:to-indigo-700"
                >
                  <span className="flex items-center">
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create a Room
                  </span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredRooms.map(room => (
                  <div 
                    key={room.id} 
                    className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden transition-all duration-300 hover:-translate-y-1 group"
                    onClick={() => handleJoinPublicRoom(room.id)}
                  >
                    <div className="p-6 relative">
                      {/* Color accent */}
                      <div className="absolute -top-8 -right-8 w-16 h-16 bg-gradient-to-br from-blue-400/30 to-indigo-400/30 dark:from-blue-600/30 dark:to-indigo-600/30 rounded-full blur-xl transform scale-0 group-hover:scale-100 transition-transform duration-300"></div>
                      
                      <div className="flex justify-between items-start mb-4 relative">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-1 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" title={room.name}>
                            {room.name}
                          </h3>
                          <p className="text-gray-500 dark:text-gray-400 text-sm">
                            Created by <span className="font-medium">{room.createdBy}</span>
                          </p>
                        </div>
                        <div className="flex-shrink-0 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                          <UserGroupIcon className="h-6 w-6 text-blue-500 dark:text-blue-400" />
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm pt-4 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex items-center text-gray-500 dark:text-gray-400">
                          <UsersIcon className="h-4 w-4 mr-1" />
                          <span className="font-medium">{room.activeUsers || 0} active</span>
                        </div>
                        
                        <div className="flex items-center text-gray-500 dark:text-gray-400">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          <span>{new Date(room.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 text-white py-3 px-6 text-center font-medium group-hover:from-blue-600 group-hover:to-indigo-700 transition-all flex items-center justify-center">
                      <span>Join Room</span>
                      <svg className="w-4 h-4 ml-2 transform translate-x-0 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Private Room Tab */}
        {activeTab === 'private' && (
          <div className="animate-fade-in max-w-xl mx-auto">
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8 relative overflow-hidden">
              {/* Background elements */}
              <div className="absolute -top-20 -right-20 w-48 h-48 bg-purple-400/20 dark:bg-purple-600/20 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-indigo-400/20 dark:bg-indigo-600/20 rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30 mb-4 shadow-inner">
                    <LockClosedIcon className="h-10 w-10 text-purple-500 dark:text-purple-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                    Join Private Room
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    Enter the room name and PIN to join a private conversation
                  </p>
                </div>

                <form onSubmit={handleJoinPrivateRoom} className="space-y-6">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 pl-1">
                      Room Name
                    </label>
                    <input
                      type="text"
                      value={privateRoomName}
                      onChange={(e) => setPrivateRoomName(e.target.value)}
                      className="w-full px-5 py-4 rounded-xl border-2 border-gray-200 dark:border-gray-600 
                      bg-white dark:bg-gray-700 text-gray-800 dark:text-white 
                      focus:border-purple-500 dark:focus:border-purple-400 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-900/50 focus:outline-none transition-all"
                      placeholder="Enter room name"
                      required
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 pl-1">
                      Room PIN (4 digits)
                    </label>
                    <input
                      type="text"
                      value={privateRoomPin}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setPrivateRoomPin(value);
                      }}
                      className="w-full px-5 py-4 rounded-xl border-2 border-gray-200 dark:border-gray-600 
                      bg-white dark:bg-gray-700 text-gray-800 dark:text-white 
                      focus:border-purple-500 dark:focus:border-purple-400 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-900/50 focus:outline-none transition-all
                      tracking-widest text-center text-xl font-mono"
                      placeholder="••••"
                      required
                      pattern="\d{4}"
                      maxLength={4}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 pl-1 mt-1">
                      The PIN is required for accessing private rooms
                    </p>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={joinPrivateLoading || !privateRoomName.trim() || privateRoomPin.length !== 4}
                    className="w-full py-4 px-6 mt-4 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 
                    text-white font-medium shadow-lg hover:shadow-xl hover:shadow-purple-500/20 dark:hover:shadow-purple-600/20
                    hover:from-purple-600 hover:to-indigo-700 transform hover:-translate-y-0.5
                    focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 
                    disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none transition-all"
                  >
                    {joinPrivateLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Joining Room...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        Join Private Room
                        <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </span>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
            <a 
              href="/admin" 
              className="text-purple-600 dark:text-purple-400 hover:underline flex items-center justify-center gap-1"
            >
              <SparklesIcon className="h-4 w-4" />
              <span>View Chat Statistics</span>
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default JoinRoom; 